// Where the hint card goes, given where its target is.
//
// **Pure, and its own file, because it is the part that is worth testing and
// the part that cannot be tested where it is used.** Everything else about the
// overlay needs a browser, a renderer and a camera; this needs four numbers.
//
// The order — right, left, below, above, then clamped — is the one Tauric
// Studio's tour uses (`studio/tour/useTargetRect.ts`), which took it from
// osts, which took it from react-tiny-popover. What is different here is the
// **bounds**: the tour dims the whole window and may place its card anywhere,
// and this one may not, because the panel and the header are the parts of the
// screen a hint is specifically not allowed to cover.

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Which way the beak points — never "which edge it is on", which inverts. */
export type Beak = "left" | "right" | "up" | "down" | "none";

export interface Placement {
  left: number;
  top: number;
  beak: Beak;
  /**
   * Where along the card's edge the beak sits, in px from the card's top-left
   * corner — down the edge for `left`/`right`, across it for `up`/`down`.
   *
   * Carried rather than centred, because the card is clamped into `bounds` and
   * a centred beak on a clamped card points somewhere its target is not.
   */
  beakAt: number;
}

/** How near a corner the beak may sit before it stops looking attached. */
const BEAK_INSET = 18;

const clamp = (value: number, low: number, high: number): number =>
  Math.max(low, Math.min(high, value));

/**
 * Place the card beside the target, inside `bounds`.
 *
 * With no target it is **parked** at the bottom-left of the free area rather
 * than hidden: the sentence is still worth reading when the thing it is about
 * has gone behind the panel or off the edge, and a hint that vanishes as the
 * camera drifts reads as a bug. `beak: "none"` is how the overlay knows not to
 * draw a pointer at nothing.
 */
export function placeHintCard(
  target: Rect | null,
  card: { width: number; height: number },
  bounds: Rect,
  gap = 14
): Placement {
  const right = bounds.x + bounds.width;
  const bottom = bounds.y + bounds.height;

  if (!target) {
    return {
      left: Math.round(bounds.x + gap),
      top: Math.round(bottom - card.height - gap),
      beak: "none",
      beakAt: 0
    };
  }

  const midX = target.x + target.width / 2;
  const midY = target.y + target.height / 2;

  const fitsRight = target.x + target.width + gap + card.width <= right;
  const fitsLeft = target.x - gap - card.width >= bounds.x;
  const fitsBelow = target.y + target.height + gap + card.height <= bottom;
  const fitsAbove = target.y - gap - card.height >= bounds.y;

  // Beside first, both ways, before anything above or below: a card to one
  // side of a piece covers a file, and one under it covers the rank the piece
  // is about to move along.
  let left: number;
  let top: number;
  if (fitsRight || fitsLeft) {
    left = fitsRight ? target.x + target.width + gap : target.x - gap - card.width;
    top = midY - card.height / 2;
  } else {
    // Neither side fits, so above or below — and when neither of those fits
    // either, below and clamped. A card overlapping its own target is the
    // readable failure; one off the screen is not a failure anybody can see.
    left = midX - card.width / 2;
    top = fitsBelow || !fitsAbove ? target.y + target.height + gap : target.y - gap - card.height;
  }

  // **Both axes, in every branch.** The sideways branch clamped only its top
  // for a while, which is right until the target is *outside* the bounds —
  // and that is the ordinary case for a control, since the panel is exactly
  // what `freeArea` cuts out. A hint about the Build tool with the panel on
  // the left put its card 46px off the left of the screen.
  left = Math.round(clamp(left, bounds.x, right - card.width));
  top = Math.round(clamp(top, bounds.y, bottom - card.height));

  return { left, top, ...beakFor(left, top, card, midX, midY) };
}

/**
 * Which edge the beak leaves by, read off where the card actually ended up.
 *
 * **Derived after the clamp, never from the branch that chose the side.** The
 * two disagree exactly when the clamp moved the card, which is the case the
 * beak is most needed for — it is what says "the thing I am about is over
 * there" when the card could not be put next to it. A target the card is
 * sitting on top of gets no beak at all, because there is no direction to
 * point.
 */
function beakFor(
  left: number,
  top: number,
  card: { width: number; height: number },
  midX: number,
  midY: number
): { beak: Beak; beakAt: number } {
  const downEdge = (): number =>
    Math.round(clamp(midX - left, BEAK_INSET, card.width - BEAK_INSET));
  const sideEdge = (): number =>
    Math.round(clamp(midY - top, BEAK_INSET, card.height - BEAK_INSET));

  if (midX < left) return { beak: "left", beakAt: sideEdge() };
  if (midX > left + card.width) return { beak: "right", beakAt: sideEdge() };
  if (midY < top) return { beak: "up", beakAt: downEdge() };
  if (midY > top + card.height) return { beak: "down", beakAt: downEdge() };
  return { beak: "none", beakAt: 0 };
}

/** Is this point inside the area the board is drawn in? */
export const inside = (bounds: Rect, x: number, y: number): boolean =>
  x >= bounds.x &&
  x <= bounds.x + bounds.width &&
  y >= bounds.y &&
  y <= bounds.y + bounds.height;

/** Same rect, to the pixel? Anchors are polled per frame; this stops rerenders. */
export const sameRect = (a: Rect | null, b: Rect | null): boolean => {
  if (a === null || b === null) return a === b;
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
};
